import React from 'react';
import { Card, Container, Divider, Stack } from '@fiverr-private/layout_components';
import { Avatar, AvatarImage } from '@fiverr-private/avatar';
import { TagCollection, Tag, TagPrefix, TagImage, ShowMoreTag, Rating } from '@fiverr-private/data_display';
import { Typography } from '@fiverr-private/typography';
import { translate } from '@fiverr-private/i18n-react';
import pathfinder from '@fiverr-private/pathfinder';
import { Button } from '@fiverr-private/button';
import { SellerCardProps } from '../../../../types/seller';
import { SELLER_BADGES } from '../SellerBadge';
import { sortSkills } from '../../../../server/utils/sortSkills';
import { FREELANCER_PAGE_PATHFINDER_KEY } from '../../../consts';
import { reportClickedOnExpertCardEvent } from '../../../utils/mixpanel';
import { AppI18n } from '../../../../i18n';

export const SellerCard = ({
    id,
    profileImageUrl,
    username,
    displayName,
    level,
    languages,
    description,
    skills,
    reviews,
    rating,
}: SellerCardProps) => {
    const sellerLevelBadge = SELLER_BADGES[level]?.() || null;
    const languagesString = languages
        .map((language) => translate(`languages.codes.${language}`.toLowerCase()))
        .join(', ');

    const sortedSkills = React.useMemo(() => sortSkills(skills), [skills]);

    const handleReddirectToSellerProfile = () => {
        reportClickedOnExpertCardEvent(Number(id), displayName, 'Vibe coding LP');
        window.open(pathfinder(FREELANCER_PAGE_PATHFINDER_KEY, { id: username }), '_blank');
    };

    return (
        <Card direction="column" padding="6" maxWidth="378px" borderRadius="xl">
            <Stack gap="4" direction="column">
                <Stack onClick={handleReddirectToSellerProfile} cursor="pointer">
                    <Avatar username={displayName} size="xl">
                        <AvatarImage src={profileImageUrl} />
                    </Avatar>
                    <Stack direction="column" alignItems="flexStart" justifyContent="flexStart">
                        <Typography fontWeight="bold" color="grey_1200">
                            {displayName}
                        </Typography>
                        {sellerLevelBadge}
                        <Typography
                            display="webkitBox"
                            webkitBoxOrient="vertical"
                            webkitLineClamp={1}
                            textOverflow="ellipsis"
                            overflow="hidden"
                        >
                            {languagesString}
                        </Typography>
                    </Stack>
                </Stack>
                <Typography
                    size="b_md"
                    display="webkitBox"
                    webkitBoxOrient="vertical"
                    webkitLineClamp={2}
                    textOverflow="ellipsis"
                    overflow="hidden"
                    onClick={handleReddirectToSellerProfile}
                >
                    {description}
                </Typography>
                <TagCollection height="90px">
                    {sortedSkills.map((skill) => (
                        <Tag key={skill.value} size="sm" variant="outline">
                            {skill.iconUrl && (
                                <TagPrefix>
                                    <TagImage src={skill.iconUrl} alt={skill.value} />
                                </TagPrefix>
                            )}
                            {skill.value}
                        </Tag>
                    ))}
                    <ShowMoreTag size="sm" variant="outline" />
                </TagCollection>
                <Stack direction="row" justifyContent="spaceBetween" gap="4">
                    <Stack alignItems="center" direction="column" gap="0.5" width="100%" flex="1" overflow="hidden">
                        <Typography size="b_xs">{`${reviews} Reviews`}</Typography>
                        <Rating score={rating} variant="compact" minimumFractionDigits={1} maximumFractionDigits={1} />
                    </Stack>
                    <Divider direction="vertical" height="42px" hidden={{ md: false, default: true }} />
                    <Stack
                        alignItems={{ default: 'center', md: 'flexEnd' }}
                        direction="column"
                        gap="0.5"
                        width="100%"
                        overflow="hidden"
                        flex="1"
                    >
                        <Container maxWidth={{ default: '150px', md: '160px' }} width="100%">
                            <Button onClick={handleReddirectToSellerProfile} fullWidth>
                                <AppI18n k="sellers_section.see_profile_button" />
                            </Button>
                        </Container>
                    </Stack>
                </Stack>
            </Stack>
        </Card>
    );
};
